import React, { Component } from 'react';
import PropTypes from 'prop-types';
import exact from 'prop-types-exact';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import {
  Button,
  ButtonToolbar,
  FormGroup,
  ControlLabel,
  FormControl,
  PanelGroup,
  Panel,
  Col,
  Row,
  Form,
} from 'react-bootstrap';
import MaskedInput from 'react-text-mask';
import {
  createNumberMask,
} from 'text-mask-addons';

import DepartmentInput from './DepartmentInput';
import DistrictInput from './DistrictInput';
import EquipmentPicker from './EquipmentPicker';
import JobInput from './JobInput';
import LeaseInput from './LeaseInput';
import WellInput from './WellInput';
import OperatorInput from './OperatorInput';
import { maybeId } from '../../utils';
import Overlay from '../Overlay';

import { enableLookupFeature } from '../../features/configuration/selectors';

import { jobEditMessage } from '../../features/forms/TicketEntry/components/TicketEntry';

export const WELL_SERVICE = 'Well Service';
export const TRUCK = 'Truck';

const propTypes = exact({
  onComplete: PropTypes.func.isRequired,
  selectedItem: PropTypes.object.isRequired,
  loading: PropTypes.bool.isRequired,
  enableLookup: PropTypes.bool.isRequired,
  lookupJoinType: PropTypes.oneOf(['user_records', 'lookup_records', 'union']),

  /* eslint-disable react/no-unused-prop-types */
  date: PropTypes.string,
  ticketNumber: PropTypes.object,
  ticketNumberData: PropTypes.object,
  /* eslint-enable react/no-unused-prop-types */
});

export class TicketEntryForm extends Component {
  static getDerivedStateFromProps(props, state) {
    const { ticketNumber, ticketNumberData, date } = props;
    const isDifferentTicketNumber = (ticketNumber !== state.ticket_number);

    if (isDifferentTicketNumber) {
      const defaultState = {
        date: '',
        department: null,
        district: null,
        ticket_number: null,
        job_number: null,
        well: null,
        equipment: [],
        lease: null,
        operator: null,
        billing_hours: null,
        billing_revenue: null,
        recommended_feedback: null,
        billing_rate: null,
        supplemental_info: '',
      };
      defaultState.ticket_number = ticketNumber;

      if (ticketNumberData) {
        const { district, department, jobNumber, operator, lease } = ticketNumberData;

        if (jobNumber) {
          const { operator: jobOperator, lease: jobLease } = jobNumber;

          defaultState.job_number = jobNumber;
          defaultState.operator = (operator) ? operator.lookup : null;
          defaultState.lease = (lease) ? lease.lookup : null;
          defaultState.operator = TicketEntryForm.getLookup(jobOperator, operator);
          const modLease = TicketEntryForm.getLookup(jobLease, lease);
          if (modLease && modLease.lease) {
            delete modLease.lease;
          }
          defaultState.lease = modLease;
        }

        defaultState.date = date;
        defaultState.department = department || null;
        defaultState.district = district || null;
      }
      return defaultState;
    }

    const currentState = state;
    if (date !== state.date && !isDifferentTicketNumber) {
      currentState.date = date;
    }

    return currentState;
  }

  static getLookup(primary, secondary) {
    if (primary && primary.lookup) {
      return primary.lookup;
    }
    if (secondary && secondary.lookup) {
      return secondary.lookup;
    }
    return null;
  }

  constructor(props) {
    super(props);

    this.state = this.handleSetDefault();

    this.handleChange = this.handleChange.bind(this);
    this.handleValueChange = this.handleValueChange.bind(this);
    this.handleComplete = this.handleComplete.bind(this);
    this.handleSetDefault = this.handleSetDefault.bind(this);
    this.handleNetRevenue = this.handleNetRevenue.bind(this);
    this.dollarValueRequireUpdate = this.dollarValueRequireUpdate.bind(this);
  }

  UNSAFE_componentWillReceiveProps(ownProps) {
    const { selectedItem } = this.props;

    /* NOTE: if selected submission item changes, state should be cleared. */
    if (selectedItem && (selectedItem.id !== ownProps.selectedItem.id)) {
      const newState = this.handleSetDefault();
      this.setState({ ...newState });
    }
  }

  handleSetDefault() {
    return {
      date: '',
      department: null,
      district: null,
      ticket_number: null,
      job_number: null,
      well: null,
      equipment: [],
      lease: null,
      operator: null,
      billing_hours: null,
      billing_revenue: null,
      recommended_feedback: null,
      billing_rate: null,
      supplemental_info: '',
    };
  }

  handleChange(e) {
    const { name, value } = e.target;

    this.setState((prevState) => {
      const prevValue = prevState[name];

      if (name === 'job_number') {
        if (value === null) {
          return {
            job_number: null,
            operator: null,
            lease: null,
            well: null,
          };
        }
        const { lookup: leLookup } = value.lease;
        const { lookup: opLookup } = value.operator;

        return {
          job_number: value,
          operator: opLookup,
          lease: leLookup,
          well: null,
        };
      }

      if (prevValue && (name === 'operator' || name === 'lease')) {
        if (prevValue !== value && name === 'operator') {
          return ({ [name]: value, lease: null, well: null });
        } if (prevValue !== value && name === 'lease') {
          return ({ [name]: value, well: null });
        }
      }

      if (name === 'billing_hours' && prevState && prevState.billing_rate) {
        const calcBillingRevenue = String(value * prevState.billing_rate);
        return ({ [name]: value, billing_revenue: calcBillingRevenue });
      }

      if (name === 'billing_rate' && prevState && prevState.billing_hours) {
        const calcBillingRevenue = String(value * prevState.billing_hours);
        return ({ [name]: value, billing_revenue: calcBillingRevenue });
      }

      return ({ [name]: value });
    });
  }

  handleValueChange(e) {
    const { name, value } = e.target;
    this.handleChange({
      target: {
        name,
        value,
      },
    });
  }

  handleNetRevenue(e) {
    const { name, value } = e.target;

    const numberWithoutSymbols = this.removeSymbols(value);
    const requiresUpdate = this.dollarValueRequireUpdate(numberWithoutSymbols);

    if (requiresUpdate) {
      this.setState({ [name]: numberWithoutSymbols });
    }
  }

  dollarValueRequireUpdate(stringValue) {
    const floatValue = parseFloat(stringValue, 10);
    return !Number.isNaN(floatValue);
  }

  handleShowBillableHours() {
    const { department } = this.state;
    let result = false;
    if (department) {
      result = (department.name === WELL_SERVICE || department.name === TRUCK);
    }

    return result;
  }

  handleComplete() {
    const currentState = Object.assign({}, this.state);
    /* NOTE: parsing the string values that are displayed in the input fields
     * to floats that the API is expecting */

    /* NOTE: removing billing_rate from ticketForm object. Not part of API */
    delete currentState.billing_rate;

    if (!this.handleShowBillableHours()) {
      currentState.billing_hours = null;
    }

    if (currentState.billing_hours) {
      const numberBillingHours = this.removeSymbols(currentState.billing_hours);
      currentState.billing_hours = parseFloat(numberBillingHours, 10);
    }

    if (currentState.billing_revenue) {
      const numberBillingRevenue = this.removeSymbols(currentState.billing_revenue);
      currentState.billing_revenue = parseFloat(numberBillingRevenue, 10);
    }

    if (currentState.job_number) {
      currentState.job_number = {
        id: currentState.job_number.id,
        number: currentState.job_number.number,
      };
    }

    this.props.onComplete(currentState);
  }

  removeSymbols(inputValue) {
    return inputValue.replace(/[$,]/g, '');
  }

  render() {
    const {
      department,
      job_number: jobNumber,
      operator,
      lease,
      district,
    } = this.state;

    const hasFormJob = (jobNumber) ? !!jobNumber.job : false;
    const disableOperator = hasFormJob;
    const disableLease = (hasFormJob && !!operator) || !operator;
    const disableWell = !lease;
    const showBillableHours = this.handleShowBillableHours();
    const districtId = maybeId(district);
    const departmentId = maybeId(department);

    const { loading, lookupJoinType, enableLookup } = this.props;

    return (
      <React.Fragment>
        <PanelGroup>
          <Panel>
            <Form>
              <Row>
                <Col md={6}>
                  <FormGroup>
                    <ControlLabel>District</ControlLabel>
                    <DistrictInput
                      departmentId={departmentId}
                      name="district"
                      value={this.state.district}
                      onChange={this.handleChange}
                    />
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <ControlLabel>Department</ControlLabel>
                    <DepartmentInput
                      districtId={districtId}
                      name="department"
                      value={department}
                      onChange={this.handleChange}
                    />
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <FormGroup>
                    <ControlLabel>Job Number</ControlLabel>
                    <JobInput
                      name="job_number"
                      value={this.state.job_number}
                      onChange={this.handleChange}
                    />
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <ControlLabel>Equipment</ControlLabel>
                    <EquipmentPicker
                      name="equipment"
                      value={this.state.equipment}
                      onChange={this.handleChange}
                    />
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <FormGroup>
                    <ControlLabel>Operator</ControlLabel>
                    <Overlay enabled={hasFormJob} message={jobEditMessage}>
                      <OperatorInput
                        disabled={disableOperator}
                        name="operator"
                        value={this.state.operator}
                        onChange={this.handleChange}
                        lookupJoinType="user_records"
                        enableLookup={enableLookup}
                      />
                    </Overlay>
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <ControlLabel>Lease</ControlLabel>
                    <Overlay enabled={hasFormJob} message={jobEditMessage}>
                      <LeaseInput
                        disabled={disableLease}
                        name="lease"
                        value={this.state.lease}
                        onChange={this.handleChange}
                        operator={this.state.operator}
                        lookupJoinType={lookupJoinType}
                      />
                    </Overlay>
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <FormGroup>
                    <ControlLabel>Well</ControlLabel>
                    <WellInput
                      disabled={disableWell}
                      name="well"
                      value={this.state.well}
                      onChange={this.handleChange}
                      lease={this.state.lease}
                      lookupJoinType={lookupJoinType}
                    />
                  </FormGroup>
                </Col>
              </Row>
            </Form>
          </Panel>
        </PanelGroup>
        <PanelGroup>
          <Panel>
            {showBillableHours
              && (
              <div>
                <FormGroup>
                  <ControlLabel>Billable Hours</ControlLabel>
                  <FormControl
                    componentClass={MaskedInput}
                    placeholder="00.0"
                    value={this.state.billing_hours}
                    name="billing_hours"
                    onChange={this.handleValueChange}
                    type="text"
                    guide={false}
                    mask={createNumberMask({ prefix: '', allowDecimal: true })}
                  />
                </FormGroup>
                <FormGroup>
                  <ControlLabel>Billing Rate</ControlLabel>
                  <FormControl
                    componentClass={MaskedInput}
                    placeholder="00.0"
                    value={this.state.billing_rate}
                    name="billing_rate"
                    onChange={this.handleValueChange}
                    type="text"
                    guide={false}
                    mask={createNumberMask({ prefix: '', allowDecimal: true })}
                  />
                </FormGroup>
              </div>
              )}
            <FormGroup>
              <ControlLabel>Net Revenue</ControlLabel>
              <FormControl
                componentClass={MaskedInput}
                placeholder="$0.00"
                name="billing_revenue"
                value={this.state.billing_revenue}
                onChange={this.handleNetRevenue}
                type="text"
                guide={false}
                mask={createNumberMask({ prefix: '$', allowDecimal: true, allowNegative: true, allowLeadingZeros: true })}
              />
            </FormGroup>
          </Panel>
        </PanelGroup>
        <PanelGroup>
          <Panel>
            <FormGroup>
              <ControlLabel>Supplemental Notes </ControlLabel>
              <FormControl
                type="textarea"
                componentClass="textarea"
                name="supplemental_info"
                onChange={this.handleChange}
                value={this.state.supplemental_info}
              />
            </FormGroup>
          </Panel>
        </PanelGroup>

        <ButtonToolbar>
          <Button
            bsStyle="primary"
            className="pull-right"
            disabled={!this.state.date || !this.state.ticket_number}
            onClick={this.handleComplete}
            title={!this.state.date || !this.state.ticket_number ? 'Date and Ticket Number are required' : ''}
          >
            Complete
          </Button>
        </ButtonToolbar>
        {loading
        && (
        <div className="text-center">
          <span className="glyphicon glyphicon-refresh spinning" /> Loading...
        </div>
        )}
      </React.Fragment>
    );
  }
}

TicketEntryForm.propTypes = propTypes;

const mapStateToProps = (state) => {
  const enableLookup = enableLookupFeature(state);
  const lookupJoinType = enableLookup ? 'union' : 'user_records';
  return {
    lookupJoinType,
    enableLookup,
  };
};

const withRedux = connect(mapStateToProps, {});

const enhance = compose(withRedux);

export default enhance(TicketEntryForm);
